/**
 * SelectionCard — the organiser's view of the rulebook's team-selection rules
 * across the event (phase 5): players with no category (A to D) when a rule
 * needs one, and each team's players against the minimum matches, with
 * "Excuse" for injuries and the like. Shows nothing when no rule applies.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { axiosInstance } from '../../config/axios';
import { errorText } from './api';

export default function SelectionCard({ event, canManage }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);         // team shown in full
  const [busy, setBusy] = useState(false);
  const base = `/v1/events/${event.id}/selection`;

  const load = useCallback(() => {
    axiosInstance.get(`${base}/min-matches`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [base]);
  useEffect(() => { load(); }, [load]);

  if (!data || (!data.rule && !(data.uncategorised || []).length)) return null;

  const excuse = async (team, p) => {
    const reason = window.prompt(`Excuse ${p.name} from the minimum matches? Give a reason:`);
    if (!reason?.trim()) return;
    setBusy(true);
    try {
      await axiosInstance.post(`${base}/excuse`, { team_id: team.team_id, player_pool_id: p.player, reason: reason.trim() });
      toast.success(`${p.name} excused`);
      load();
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  const unexcuse = async (p) => {
    setBusy(true);
    try {
      await axiosInstance.delete(`${base}/excuse/${p.excuse_id}`);
      load();
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-3" data-testid="selection-card">
      <p className="font-bold text-sm"><i className="ph-bold ph-list-checks" /> Team selection rules</p>

      {(data.uncategorised || []).length > 0 && (
        <div className="rounded-xl bg-[#FDF5D3] px-3 py-2 text-sm text-[#2B3A67]" data-testid="uncategorised">
          <p className="font-bold">{data.uncategorised.length} player{data.uncategorised.length > 1 ? 's' : ''} without a category</p>
          <p className="text-xs">Category rules can't be checked for them. Set A, B, C or D on their registration.</p>
          <p className="text-xs mt-1">{data.uncategorised.slice(0, 8).map((p) => `${p.name} (${p.team_name})`).join(', ')}{data.uncategorised.length > 8 ? '…' : ''}</p>
        </div>
      )}

      {data.rule && (
        <div>
          <p className="text-xs font-bold text-gray-500 mb-1.5">
            At least {data.teams[0]?.needed} matches each
          </p>
          <ul className="divide-y divide-gray-100">
            {data.teams.map((t) => {
              const short = t.players.filter((p) => p.short);
              const stuck = short.filter((p) => !p.can_reach);
              return (
                <li key={t.team_id} className="py-2">
                  <button type="button" onClick={() => setOpen(open === t.team_id ? null : t.team_id)}
                    className="w-full flex items-center justify-between text-sm text-left">
                    <span className="font-semibold">{t.team_name}</span>
                    <span className="text-xs flex items-center gap-2">
                      {t.points_lost > 0 && <span className="font-bold text-[#B3263F]">−{t.points_lost} pts</span>}
                      {stuck.length > 0 && !t.checkpoint_reached && <span className="font-bold text-[#B3263F]">{stuck.length} can't make it</span>}
                      <span className="text-gray-500">{short.length ? `${short.length} short` : 'all on track'}</span>
                      <i className={`ph-bold ${open === t.team_id ? 'ph-caret-up' : 'ph-caret-down'}`} />
                    </span>
                  </button>
                  {open === t.team_id && (
                    <ul className="mt-2 space-y-1">
                      {t.players.map((p) => (
                        <li key={p.player} className="flex items-center justify-between text-xs gap-2">
                          <span className="truncate">
                            {p.name} <span className={p.short ? 'text-[#B3263F] font-bold' : 'text-gray-500'}>{p.played} of {p.needed}</span>
                            {p.excused && <span className="ml-1 text-gray-500">(excused)</span>}
                          </span>
                          {canManage && (p.excused
                            ? <button type="button" disabled={busy} onClick={() => unexcuse(p)} className="text-[#1F7A75] font-semibold">Undo</button>
                            : p.short && <button type="button" disabled={busy} onClick={() => excuse(t, p)} className="text-[#1F7A75] font-semibold">Excuse</button>)}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
          <p className="text-[11px] text-gray-500 mt-1">Counted from locked squads of finished matches; points are lost at the rule's checkpoint.</p>
        </div>
      )}
    </div>
  );
}
